import { WatchBudgetStore } from './watchBudget';
import { ensureYouTubeApi } from './youtubeApi';

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  parts.push(`${remainder}s`);

  return parts.join(' ');
}

export class VideoModalController {
  private openVersion = 0;
  private selectedVideoId: string | null = null;
  private readonly retryButton: HTMLButtonElement;
  private readonly playerHost: HTMLElement;
  private player: YT.Player | null = null;
  private playbackTimer: number | null = null;
  private readonly budget: WatchBudgetStore;

  constructor(private readonly modal: HTMLElement, maxDailySeconds = 0) {
    this.playerHost = this.modal.querySelector('#youtubePlayerContainer')!.parentElement!;
    this.retryButton = this.modal.querySelector<HTMLButtonElement>('.video-modal-retry')!;
    this.retryButton.addEventListener('click', () => {
      if (this.selectedVideoId) void this.open(this.selectedVideoId);
    });
    this.budget = new WatchBudgetStore(localStorage, maxDailySeconds);
    const closeButton = this.modal.querySelector<HTMLButtonElement>('.video-modal-close');
    closeButton?.addEventListener('click', () => this.close());

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.style.display === 'flex') {
        this.close();
      }
    });
  }

  setMaxDailySeconds(maxDailySeconds: number): void {
    this.budget.setMaxDailySeconds(maxDailySeconds);
    this.updateTimeLeft();
  }

  getTimeLeft(): number {
    return this.budget.getTimeLeft();
  }

  getWatchedSeconds(): number {
    return this.budget.getWatchedSeconds();
  }

  resetWatchBudget(): void {
    this.budget.reset();
    this.updateTimeLeft();
  }

  async open(videoId: string): Promise<void> {
    const version = ++this.openVersion;
    this.selectedVideoId = videoId;
    this.cleanupPlayer();
    this.modal.style.display = 'flex';
    this.retryButton.hidden = true;
    try {
      this.updateTimeLeft();
      if (this.budget.getTimeLeft() <= 0) {
        this.showError('Daily video limit reached.');
        return;
      }

      this.showError('Loading video…');
      await ensureYouTubeApi();
      if (version !== this.openVersion) return;
      if (!window.YT?.Player) throw new Error('YouTube player is unavailable.');
      this.showError('');

      this.player = new window.YT.Player('youtubePlayerContainer', {
        height: '100%',
        width: '100%',
        videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          autoplay: 1,
          controls: 1,
          enablejsapi: 1,
          fs: 1,
          playsinline: 1,
          rel: 0,
        },
        events: {
          onStateChange: (event: YT.OnStateChangeEvent) => this.handlePlayerStateChange(event),
          onError: () => this.showError('Unable to play this video right now.'),
        },
      });
    } catch {
      if (version !== this.openVersion) return;
      this.showError('Unable to load YouTube. Check your connection or website restrictions, then try again.');
      this.retryButton.hidden = false;
    }
  }

  close(): void {
    ++this.openVersion;
    this.cleanupPlayer();
    this.modal.style.display = 'none';
  }

  private handlePlayerStateChange(event: YT.OnStateChangeEvent): void {
    const playerState = window.YT?.PlayerState;
    if (!playerState) {
      return;
    }

    if (event.data === playerState.PLAYING) {
      this.playbackTimer = window.setInterval(() => {
        this.budget.increment();
        this.updateTimeLeft();
        if (this.budget.getTimeLeft() <= 0) {
          this.showError('Daily video limit reached.');
          this.player?.stopVideo();
          this.cleanupPlayer();
        }
      }, 1_000);
      return;
    }

    if (this.playbackTimer) {
      window.clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }

    if (event.data === playerState.ENDED) {
      this.close();
    }
  }

  private cleanupPlayer(): void {
    if (this.playbackTimer) {
      window.clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }

    this.player?.destroy();
    this.player = null;
    this.playerHost.querySelector('#youtubePlayerContainer')?.remove();
    const container = document.createElement('div');
    container.id = 'youtubePlayerContainer';
    container.className = 'absolute inset-0';
    this.playerHost.prepend(container);
  }

  private showError(message: string): void {
    const errorElement = document.getElementById('videoErrorMessage');
    if (!errorElement) {
      return;
    }

    errorElement.textContent = message;
    errorElement.style.display = message ? 'block' : 'none';
  }

  private updateTimeLeft(): void {
    const timeLeft = document.getElementById('videoPlaybackTime');
    if (timeLeft) {
      timeLeft.textContent = formatTime(this.budget.getTimeLeft());
    }
  }
}
